import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Home, BookOpen, MessageSquare, Plus, LogOut, User } from 'lucide-react';
import { Logo } from './Logo';

export interface NavItem {
  icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  label: string;
  path: string;
}

/** Same four destinations, same icons, as mobile's tab bar. */
export const NAV_ITEMS: NavItem[] = [
  { icon: Home, label: 'Home', path: '/dashboard' },
  { icon: BookOpen, label: 'Library', path: '/library' },
  { icon: MessageSquare, label: 'Workspace', path: '/workspace' },
  { icon: User, label: 'Account', path: '/account' },
];

export const isActivePath = (pathname: string, path: string): boolean =>
  pathname === path || (path === '/library' && pathname.startsWith('/library/'));

const EASE = 'cubic-bezier(0.33,1,0.68,1)';

/**
 * Desktop navigation — a floating white rail lying on the paper, the desktop
 * cousin of mobile's floating pill tab bar: lilac capsule behind the active
 * item, accent icon, and an ink "Scan notes" button. Hidden below `md`, where
 * the TabBar takes over. `collapsed` shrinks it to icons (Workspace).
 */
export const Sidebar = React.memo<{ collapsed?: boolean }>(({ collapsed = false }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  if (!user) return null;

  return (
    <aside
      className={`fixed bottom-3 left-3 top-3 z-50 hidden flex-col rounded-[1.75rem] border border-solid border-outline-variant bg-surface-container-lowest py-6 shadow-[0_10px_24px_rgba(28,27,25,0.08)] md:flex ${
        collapsed ? 'w-[4.5rem] items-center px-2.5' : 'w-[15.5rem] px-4'
      }`}
      style={{ transition: `width 300ms ${EASE}, padding 300ms ${EASE}` }}
    >
      <button
        type="button"
        onClick={() => navigate('/dashboard')}
        className={`flex cursor-pointer items-center gap-2.5 border-none bg-transparent p-0 ${collapsed ? '' : 'px-2'}`}
        aria-label="PaperBrain home"
      >
        <Logo size={collapsed ? 34 : 36} />
        {!collapsed && (
          <span className="mt-[0.15em] whitespace-nowrap font-headline text-[1.5rem] font-medium italic text-on-surface">
            Paper<span className="pb-wordmark-grad">Brain</span>
          </span>
        )}
      </button>

      <nav className="mt-9 flex w-full flex-col gap-1">
        {NAV_ITEMS.map(({ icon: Icon, label, path }) => {
          const active = isActivePath(location.pathname, path);
          return (
            <button
              key={path}
              type="button"
              onClick={() => navigate(path)}
              title={collapsed ? label : undefined}
              aria-current={active ? 'page' : undefined}
              className={`flex w-full cursor-pointer items-center gap-3 rounded-full border-none font-body text-sm transition-colors duration-150 ${
                collapsed ? 'h-11 justify-center p-0' : 'px-4 py-2.5'
              } ${
                active
                  ? 'bg-surface-highlight font-semibold text-accent'
                  : 'bg-transparent font-medium text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface'
              }`}
            >
              <Icon size={20} strokeWidth={active ? 2.25 : 2} />
              {!collapsed && <span>{label}</span>}
            </button>
          );
        })}
      </nav>

      <div className="mt-auto flex w-full flex-col gap-2">
        <button
          type="button"
          onClick={() => navigate('/scanner')}
          title={collapsed ? 'Scan notes' : undefined}
          className={`pb-btn pb-btn--primary shadow-[0_3px_6px_rgba(28,27,25,0.25)] ${collapsed ? 'h-12 w-12 self-center p-0' : 'w-full py-3'}`}
        >
          <Plus size={collapsed ? 22 : 18} strokeWidth={2.5} />
          {!collapsed && <span>Scan notes</span>}
        </button>
        <button
          type="button"
          onClick={() => navigate('/logout')}
          title={collapsed ? 'Sign out' : undefined}
          className={`pb-btn pb-btn--ghost text-on-surface-variant ${collapsed ? 'h-10 w-10 self-center p-0' : 'w-full py-2 text-[0.8125rem] font-medium'}`}
        >
          <LogOut size={16} />
          {!collapsed && <span>Sign out</span>}
        </button>
      </div>
    </aside>
  );
});
